import React from "react";
import { Trash2, Loader2, Video, Wand2, Lock } from "lucide-react";
import { useTheme } from "@context/ThemeContext";
import {
  getProjectVideoProvider,
  supportsVideoTaskCancel,
} from "@utils/projectRuntime";

const ShotCardGrid = React.memo(({
  scene,
  index,
  currentRatio,
  isGlobalLocked,
  hoveredGridIndex,
  setHoveredGridIndex,
  handleDeleteRow,
  handleGenerateImage,
  stopImageGeneration,
  stopVideoGeneration,
  setPreviewImage,
  projectId,
  videoEnabled = true,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const aspectRatio = currentRatio.replace(":", "/");
  const storedVideoProvider = scene.video_provider || scene.videoProvider || scene.video_model || scene.videoModel;
  const canCancelVideoTask = supportsVideoTaskCancel(
    getProjectVideoProvider(projectId, storedVideoProvider) || storedVideoProvider,
  );

  return (
    <div
      className="group relative rounded-lg overflow-hidden transition-all duration-200 flex flex-col cursor-pointer border border-[var(--color-border-secondary)] hover:border-[color-mix(in_srgb,var(--color-accent-primary)_50%,transparent)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
      style={{
        backgroundColor: 'var(--color-bg-card)',
      }}
      onMouseEnter={() => setHoveredGridIndex(index)}
      onMouseLeave={() => setHoveredGridIndex(null)}
    >
      {/* 顶部 Header */}
      <div
        className="p-2 flex items-center justify-between"
        style={{ backgroundColor: 'var(--color-bg-tertiary)', borderBottom: '1px solid var(--color-border-secondary)' }}
      >
        <div className="flex items-center gap-1.5">
          <div
            className="h-5 px-1.5 rounded flex items-center justify-center text-2xs font-bold min-w-[1.5rem]"
            style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: theme.colors.accentPrimary }}
          >
            #{scene.shot_number}
          </div>
          <span className="text-2xs font-mono" style={{ color: 'var(--color-text-muted)', opacity: 0.5 }}>id:{scene.id}</span>
          <span
            className="text-2xs px-1.5 rounded"
            style={{ backgroundColor: 'var(--color-accent-light)', color: theme.colors.accentSecondary }}
          >
            {scene.shot || '-'}
          </span>
          <span
            className="text-2xs px-1.5 rounded"
            style={{ backgroundColor: 'var(--color-blue-light)', color: theme.colors.bluePrimary }}
          >
            {scene.time || '-'}
          </span>
        </div>
        {isGlobalLocked ? (
            <Lock size={12} className="opacity-0 group-hover:opacity-100 cursor-not-allowed" style={{ color: 'var(--color-text-muted)' }} title="任务进行中"/>
        ) : (
            <button
              onClick={() => handleDeleteRow(index)}
              className="opacity-40 hover:opacity-100 focus:opacity-100 transition-all duration-200 p-1 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-error)] hover:text-[var(--color-accent-contrast)]"
              aria-label="删除镜头"
              title="删除镜头"
            >
              <Trash2 size={12} />
            </button>
        )}
      </div>

      {/* 图片/视频 显示区域 */}
      <div
        className="w-full relative overflow-hidden"
        style={{ aspectRatio, backgroundColor: 'var(--color-bg-overlay)' }}
      >
        {/* Loading 遮罩 */}
        {(scene.isGenerating || (videoEnabled && scene.isGeneratingVideo)) && (
          <div
            className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-2 animate-in fade-in duration-300"
            style={{ backgroundColor: 'var(--overlay-heavy)', color: theme.colors.accentPrimary }}
          >
            <Loader2 size={24} className="animate-spin" />
            {((videoEnabled && scene.isGeneratingVideo && canCancelVideoTask) || scene.isGenerating) && (
              <button
                onClick={() => {
                  if (videoEnabled && scene.isGeneratingVideo && canCancelVideoTask && stopVideoGeneration) {
                    stopVideoGeneration(index);
                  } else if (scene.isGenerating && stopImageGeneration) {
                    stopImageGeneration(index);
                  }
                }}
                className="px-3 py-1 rounded text-2xs transition-colors duration-150 bg-[var(--color-red-light)] text-[var(--color-error)] hover:bg-[var(--color-error)] hover:text-[var(--color-accent-contrast)]"
              >
                停止
              </button>
            )}
          </div>
        )}
        {videoEnabled && !scene.isGeneratingVideo && scene.video_task_status === 'failed' && scene.video_task_error && (
          <div
            className="absolute left-2 right-2 bottom-2 z-40 px-2 py-1 rounded text-2xs truncate"
            style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)', border: '1px solid var(--color-error)' }}
            title={scene.video_task_error}
          >
            视频失败：{scene.video_task_error}
          </div>
        )}

        {/* 视频自动播放 (Hover 时) */}
        {videoEnabled && scene.generated_video && hoveredGridIndex === index && (
          <video
            key={scene.generated_video}
            src={scene.generated_video}
            className="absolute inset-0 w-full h-full object-cover z-20"
            autoPlay
            muted
            playsInline
          />
        )}

        {/* 视频图标标记 */}
        {videoEnabled && scene.generated_video && hoveredGridIndex !== index && (
          <div className="absolute top-2 right-2 p-1 rounded-full z-20" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>
            <Video size={10} />
          </div>
        )}

        {/* 图片显示 */}
        {scene.generated_image ? (
          <div className="relative w-full h-full">
            <img
              src={scene.generated_image}
              className="w-full h-full object-cover cursor-zoom-in transition-opacity duration-200 hover:opacity-90"
              onClick={() => setPreviewImage(scene.generated_image)}
              alt={`Shot ${scene.shot_number}`}
            />

            {/* 重新生成时的半透明遮罩 Loading */}
            {scene.isGenerating && (
              <div
                className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5"
                style={{ backgroundColor: 'var(--overlay-medium)', backdropFilter: 'blur(3px)' }}
              >
                <Wand2 size={18} style={{ color: theme.colors.accentPrimary }} />
                <span className="text-2xs font-medium" style={{ color: 'white' }}>
                  {scene.image_candidates?.length > 0 ? `${scene.image_candidates.length}/4` : '生成中...'}
                </span>
                <div className="w-16 h-0.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(scene.image_candidates?.length || 0) * 25}%`,
                      backgroundColor: theme.colors.accentPrimary
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            className="w-full h-full flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors duration-150 hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: 'var(--color-text-muted)' }}
            onClick={() => handleGenerateImage(index)}
          >
            <div className="p-2 rounded-full" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
              <Wand2 size={16} />
            </div>
            <span className="text-2xs font-medium">点击生成</span>
          </div>
        )}
      </div>

      {/* 底部文字 */}
      <div className="p-2" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
        <p className="text-2xs leading-tight line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
          {scene.visual_content || "无画面描述"}
        </p>
      </div>
    </div>
  );
});

export default ShotCardGrid;
